import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Contact } from "@/components/Contact";
import { SEO } from "@/components/SEO";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { Button } from "@/components/ui/button";
import { ArrowRight, Phone, Palette, Wrench, ScrollText, Home, ShieldCheck, Clock, Leaf, Lightbulb } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Link } from "react-router-dom";

const leistungen = [
  {
    icon: Palette,
    title: "Wand- & Deckenanstriche",
    text: "Wir nutzen ausschließlich hochwertige Dispersions-, Silikat- oder Latexfarben namhafter Hersteller.",
    details: [
      "Fokus: Deckkraft, Langlebigkeit und gesundheitliche Unbedenklichkeit (emissionsarm & lösemittelfrei)",
      "Service: Individuelle Farbberatung direkt bei Ihnen vor Ort"
    ]
  },
  {
    icon: Wrench,
    title: "Spachtelarbeiten (Q2 bis Q4)",
    text: "Der Standard für moderne, glatte Wände.",
    details: [
      "Q2/Q3: Die solide Basis für Tapeten oder Strukturputze",
      "Q4 (High-End): Absolut glatte Oberflächen für exklusive Lack- oder Lasurtechniken. Staubarmes Schleifen mit modernster Absaugtechnik"
    ]
  },
  {
    icon: ScrollText,
    title: "Tapezierarbeiten",
    text: "Klassisch oder modern – wir beherrschen jede Technik.",
    details: [
      "Raufaser & Vlies: Die bewährten Klassiker für saubere Flächen",
      "Design- & Fototapeten: Individuelle Muster oder großformatige Motive",
      "Glasgewebe: Robust und langlebig, ideal für Flure oder gewerbliche Räume"
    ]
  },
  {
    icon: Home,
    title: "Renovierungsservice",
    text: "Ob ein einzelnes Zimmer oder die komplette Wohnungssanierung: Wir planen den Ablauf so, dass Ihr Alltag kaum gestört wird.",
    details: [
      "Inklusive: Abdecken, Abkleben und finale Endreinigung"
    ]
  }
];

const steinVorteile = [
  {
    icon: ShieldCheck,
    title: "Sauberkeits-Garantie",
    text: "Wir schützen Ihre Möbel und Böden penibel. Unsere Baustellen verlassen wir jeden Tag besenrein."
  },
  {
    icon: Clock,
    title: "Termintreue",
    text: "Wenn wir sagen, wir kommen Montag um 8:00 Uhr, dann stehen wir vor der Tür."
  },
  {
    icon: Leaf,
    title: "Gesundes Wohnen",
    text: "Wir verwenden bevorzugt mineralische Produkte, die das Raumklima positiv beeinflussen und Allergiker-freundlich sind."
  }
];

const faqs = [
  {
    question: "Muss ich die Räume komplett leer räumen?",
    answer: "Große Möbelstücke können oft in der Raummitte verbleiben. Wir decken alles sicher mit Profi-Vlies und Folien ab."
  },
  {
    question: "Wie lange dauert das Streichen einer 3-Zimmer-Wohnung?",
    answer: "Inklusive Vorarbeiten und Trocknungszeiten planen wir meist 3 bis 5 Werktage ein."
  },
  {
    question: "Können Sie auch Nikotin- oder Wasserschäden beseitigen?",
    answer: "Ja, wir nutzen spezielle Absperrfarben, damit Flecken nicht wieder durchschlagen."
  }
];

const Malerarbeiten = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Malerarbeiten & Innenraumgestaltung",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Malerfachbetrieb Stein GmbH und Co. KG",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Mindener Str. 183",
        "addressLocality": "Herford",
        "postalCode": "32049",
        "addressRegion": "NRW",
        "addressCountry": "DE"
      },
      "telephone": "+49-5221-3832130"
    },
    "areaServed": [
      { "@type": "City", "name": "Herford" },
      { "@type": "City", "name": "Hiddenhausen" },
      { "@type": "City", "name": "Löhne" }
    ],
    "description": "Malerarbeiten in Herford: Tapezierarbeiten, Q2-Q4 Spachtelung, Wandanstriche und Renovierungsservice vom Meisterbetrieb."
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Malerarbeiten in Herford: Spachteltechnik & Anstrich"
        description="Malerarbeiten in Herford: Wandgestaltung, Tapezieren & Glattspachtel Q2–Q4 vom Malermeister. Sauber, termintreu, Festpreis-Garantie. ☎ 05221 3832130"
        keywords="Malerarbeiten Herford, Tapezieren Herford, Spachteltechnik Q3 Q4, Wände streichen Herford, Wandgestaltung Herford, Maler Herford"
        canonical="https://malerfachbetrieb-stein.de/malerarbeiten-innenraum"
        structuredData={structuredData}
      />
      <Navigation />
      <WhatsAppButton />
      
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-foreground overflow-hidden">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 right-10 w-72 h-72 border border-background/5 rotate-45" />
            <div className="absolute bottom-10 left-20 w-96 h-96 border border-background/5 rounded-full" />
          </div>
          <div className="container mx-auto relative z-10">
            <div className="max-w-3xl">
              <span className="inline-block text-sm font-semibold tracking-wider uppercase text-background/50 mb-4">
                Malerarbeiten & Innenraum
              </span>
              <h1 className="font-caslon text-3xl md:text-4xl lg:text-5xl lg:text-6xl font-bold text-background mb-6 leading-tight">
                Malerarbeiten in Meisterqualität: Frische Farben für Ihr Zuhause
              </h1>
              <p className="text-lg md:text-xl text-background/80 mb-10 leading-relaxed max-w-2xl">
                Von der perfekten Raufaser bis zur High-End Glattspachtelung. Wir bringen Farbe, 
                Struktur und Wohlbehagen in Ihre Wohn- und Geschäftsräume im Kreis Herford.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-background text-foreground hover:bg-background/90 font-semibold px-10 py-7 text-base rounded-2xl transition-all duration-500 hover:scale-105 group"
                  onClick={() => document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Jetzt unverbindliches Angebot anfordern
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
                <a href="tel:+4952213832130">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-background text-background hover:bg-background/10 font-semibold px-10 py-7 text-base rounded-2xl">
                    <Phone className="mr-2 h-5 w-5" />
                    05221 / 3832130
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Qualität im Detail */}
        <section className="py-16 md:py-24 lg:py-32 bg-background">
          <div className="container mx-auto max-w-4xl">
            <h2 className="font-caslon text-3xl md:text-4xl font-bold text-foreground mb-8">
              Mehr als nur ein Anstrich – Wir schaffen Oberflächen mit Charakter
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Malerarbeiten sind das Fundament jeder Inneneinrichtung. Doch die Qualität entscheidet 
              darüber, ob Sie sich jahrelang wohlfühlen. Als Fachbetrieb in Herford legen wir höchsten 
              Wert auf die Untergrundvorbereitung. Denn nur auf einer perfekt gespachtelten Wand kommt 
              die Farbe oder Tapete richtig zur Geltung.
            </p>
          </div>
        </section>

        {/* Leistungen im Innenraum */}
        <section className="py-16 md:py-24 lg:py-32 bg-muted/30">
          <div className="container mx-auto">
            <div className="max-w-3xl mb-16">
              <span className="text-sm font-semibold tracking-wider uppercase text-muted-foreground mb-4 block">
                Unsere Leistungen
              </span>
              <h2 className="font-caslon text-3xl md:text-4xl font-bold text-foreground">
                Unsere Leistungen im Innenraum
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {leistungen.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="bg-background rounded-3xl p-8 md:p-10">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-14 h-14 rounded-2xl bg-foreground flex items-center justify-center">
                        <Icon className="w-7 h-7 text-background" />
                      </div>
                      <h3 className="font-caslon text-xl font-bold text-foreground">{item.title}</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed mb-4">{item.text}</p>
                    <ul className="space-y-2">
                      {item.details.map((detail, i) => (
                        <li key={i} className="text-sm text-foreground font-medium flex items-start gap-2">
                          <span className="text-foreground mt-1">•</span>
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Stein-Vorteil */}
        <section className="py-16 md:py-24 lg:py-32 bg-foreground text-background">
          <div className="container mx-auto">
            <div className="max-w-3xl mb-16">
              <span className="text-sm font-semibold tracking-wider uppercase text-background/50 mb-4 block">
                Der Stein-Vorteil
              </span>
              <h2 className="font-caslon text-3xl md:text-4xl font-bold">
                Warum Kunden uns ihre Räume anvertrauen
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-px bg-background/10 rounded-3xl overflow-hidden">
              {steinVorteile.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="bg-foreground p-10 flex flex-col gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-background/10 flex items-center justify-center">
                      <Icon className="w-7 h-7 text-background" />
                    </div>
                    <h3 className="font-caslon text-xl font-bold text-background">{item.title}</h3>
                    <p className="text-background/60 leading-relaxed">{item.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Kombinations-Tipp */}
        <section className="py-16 bg-background">
          <div className="container mx-auto max-w-4xl">
            <div className="bg-muted/50 rounded-3xl p-8 md:p-12 flex gap-6 items-start">
              <Lightbulb className="w-8 h-8 text-foreground shrink-0 mt-1" />
              <div>
                <h3 className="font-caslon text-xl font-bold text-foreground mb-3">
                  Kombinieren Sie für das perfekte Ergebnis
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Wussten Sie schon? Malerarbeiten lassen sich ideal mit einer{" "}
                  <Link to="/innendaemmung-schimmelschutz" className="text-foreground font-semibold underline underline-offset-4">
                    Innendämmung
                  </Link>{" "}
                  kombinieren, um Heizkosten zu sparen, oder mit{" "}
                  <Link to="/fugenlose-baeder-oberflaechen" className="text-foreground font-semibold underline underline-offset-4">
                    edlen Oberflächen in Betonoptik
                  </Link>{" "}
                  für eine exklusive Akzentwand. Wir beraten Sie gerne zu den Kombinationsmöglichkeiten!
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 lg:py-32 bg-muted/30">
          <div className="container mx-auto max-w-3xl">
            <div className="text-center mb-16">
              <span className="text-sm font-semibold tracking-wider uppercase text-muted-foreground mb-4 block">
                Häufige Fragen
              </span>
              <h2 className="font-caslon text-3xl md:text-4xl font-bold text-foreground">
                Wissenswertes für Ihr Projekt
              </h2>
            </div>
            
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`} 
                  className="bg-background border border-foreground/10 rounded-2xl px-6"
                >
                  <AccordionTrigger className="text-left hover:no-underline py-6">
                    <span className="font-semibold pr-4">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 lg:py-32 bg-foreground">
          <div className="container mx-auto text-center max-w-3xl">
            <h2 className="font-caslon text-3xl md:text-4xl font-bold text-background mb-6">
              Bringen Sie Farbe in Ihr Projekt
            </h2>
            <p className="text-lg text-background/80 mb-10 leading-relaxed">
              Lassen Sie uns Ihre Wände neu gestalten. Rufen Sie uns an oder senden Sie uns eine 
              Nachricht für eine Besichtigung in Herford.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-background text-foreground hover:bg-background/90 font-semibold px-10 py-7 text-base rounded-2xl"
                onClick={() => document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Jetzt Termin vereinbaren
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <a href="tel:+4952213832130">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-background text-background hover:bg-background/10 font-semibold px-10 py-7 text-base rounded-2xl">
                  <Phone className="mr-2 h-5 w-5" />
                  05221 / 3832130
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Kontaktformular */}
        <Contact />
      </main>
      
      <Footer />
    </div>
  );
};

export default Malerarbeiten;
